import React, { useState, useEffect } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { url } from '../../util/config';
import { useNavigate } from 'react-router-dom';

const MAX_NAME = 50;
const MAX_DESC = 500;
const numberOperators = ['>', '<', '=', '!='];
const dropdownOperators = ['=', '!='];

function createRuleNode() {
    return { field: '', operator: '', value: '', children: [] };
}

// ── RuleNode defined OUTSIDE component to prevent focus loss on re-render ────
const RuleNode = ({ node, onChange, onDelete, isRoot, staticFields }) => {
    const fieldObj = staticFields.find(f => f.label === node.field);

    return (
        <div style={{
            borderLeft: isRoot ? '3px solid #1565c0' : '2px dashed #bfdbfe',
            marginLeft: isRoot ? 0 : 20,
            paddingLeft: 14,
            marginBottom: 12,
            background: isRoot ? '#f0f7ff' : '#f8fafc',
            borderRadius: 8,
            padding: '12px 14px',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {/* Field */}
                <select style={ruleStyles.select} value={node.field}
                    onChange={e => onChange({ ...node, field: e.target.value, operator: '', value: '', children: [] })}>
                    <option value="">Select Field</option>
                    {staticFields.map(f => <option key={f.label} value={f.label}>{f.label}</option>)}
                </select>

                {/* Operator */}
                <select style={ruleStyles.select} value={node.operator} disabled={!node.field}
                    onChange={e => onChange({ ...node, operator: e.target.value })}>
                    <option value="">Operator</option>
                    {node.field && fieldObj && fieldObj.type === 'number'
                        ? numberOperators.map(op => <option key={op} value={op}>{op}</option>)
                        : node.field && fieldObj && fieldObj.type === 'dropdown'
                            ? dropdownOperators.map(op => <option key={op} value={op}>{op}</option>)
                            : null}
                </select>

                {/* Value */}
                {node.field && fieldObj && fieldObj.type === 'dropdown' ? (
                    <select style={ruleStyles.select} value={node.value} disabled={!node.operator}
                        onChange={e => onChange({ ...node, value: e.target.value })}>
                        <option value="">Select {node.field}</option>
                        {fieldObj.options && fieldObj.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                ) : (
                    <input style={ruleStyles.input} type="number" value={node.value} disabled={!node.operator}
                        placeholder={node.field || 'Value'} min={fieldObj?.min} max={fieldObj?.max}
                        onChange={e => onChange({ ...node, value: e.target.value })} />
                )}

                {/* Actions */}
                <button style={ruleStyles.btnDelete} onClick={onDelete}>
                    <i className="bi bi-trash"></i>
                </button>
                {isRoot && (
                    <button style={ruleStyles.btnAdd}
                        onClick={() => onChange({ ...node, children: [...node.children, createRuleNode()] })}>
                        <i className="bi bi-plus" style={{ marginRight: 4 }}></i>Add Condition
                    </button>
                )}
            </div>

            {/* Children */}
            {node.children.map((child, idx) => (
                <RuleNode key={idx} node={child} staticFields={staticFields}
                    onChange={updatedChild => {
                        const newChildren = [...node.children];
                        newChildren[idx] = updatedChild;
                        onChange({ ...node, children: newChildren });
                    }}
                    onDelete={() => onChange({ ...node, children: node.children.filter((_, i) => i !== idx) })}
                    isRoot={false}
                />
            ))}
        </div>
    );
};

const ruleStyles = {
    select: { padding: '6px 10px', fontSize: '13px', border: '1.5px solid #d1d5db', borderRadius: '6px', outline: 'none', color: '#111827', background: 'white', minWidth: 120 },
    input: { padding: '6px 10px', fontSize: '13px', border: '1.5px solid #d1d5db', borderRadius: '6px', outline: 'none', color: '#111827', width: 110 },
    btnDelete: { background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: '6px', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 13 },
    btnAdd: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1565c0', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
};

// ── Validate rules helper ────────────────────────────────────────────────────
function validateRules(rules, staticFields) {
    for (const rule of rules) {
        if (!rule.field) return { valid: false, message: 'Please select a field for all rules.' };
        const fieldObj = staticFields.find(f => f.label === rule.field);
        if (!fieldObj) return { valid: false, message: `Field "${rule.field}" is not valid.` };
        if (!rule.operator) return { valid: false, message: `Please select an operator for "${rule.field}".` };
        if (fieldObj.type === 'dropdown') {
            if (!rule.value || !fieldObj.options.includes(rule.value))
                return { valid: false, message: `Please select a valid value for "${rule.field}".` };
        } else if (fieldObj.type === 'number') {
            if (rule.value === '' || isNaN(Number(rule.value)) || Number(rule.value) < fieldObj.min || (fieldObj.max && Number(rule.value) > fieldObj.max))
                return { valid: false, message: `Please enter a valid number for "${rule.field}".` };
        }
        if (rule.children && rule.children.length > 0) {
            const childResult = validateRules(rule.children, staticFields);
            if (!childResult.valid) return childResult;
        }
    }
    return { valid: true };
}

// ── Rule Tree Preview ────────────────────────────────────────────────────────
const RuleTreePreview = ({ rules }) => {
    if (rules.length === 0) return <p style={{ color: '#9ca3af', fontSize: 13, margin: 0 }}>No rules added yet.</p>;
    const render = (nodes, level = 0) => (
        <ul style={{ listStyle: 'none', paddingLeft: level * 14, margin: 0 }}>
            {nodes.map((node, idx) => (
                <li key={idx} style={{ marginBottom: 6 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {node.field && <strong style={{ color: '#1565c0', fontSize: 13 }}>{node.field}</strong>}
                        {node.operator && <span style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4, fontSize: 12, color: '#6b7280' }}>{node.operator}</span>}
                        {node.value && <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 13 }}>{node.value}</span>}
                    </span>
                    {node.children && node.children.length > 0 && render(node.children, level + 1)}
                </li>
            ))}
        </ul>
    );
    return render(rules);
};

// ── Main Component ───────────────────────────────────────────────────────────
const CreateSegmentComponent = () => {
    const navigate = useNavigate();
    const [rules, setRules] = useState([]);
    const [staticFields, setStaticFields] = useState([]);
    const [segmentName, setSegmentName] = useState('');
    const [segmentDesc, setSegmentDesc] = useState('');
    const [nameError, setNameError] = useState('');
    const [descError, setDescError] = useState('');
    const [ruleError, setRuleError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchFields = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${url}/segmentmaster`, { headers: { Authorization: `Bearer ${token}` } });
                if (!response.ok) throw new Error('Failed to fetch segment master');
                const data = await response.json();
                setStaticFields(data);
            } catch { setStaticFields([]); }
        };
        fetchFields();
    }, []);

    const handleSubmit = async () => {
        setRuleError('');
        if (!segmentName.trim()) { setNameError('Segment name is required.'); return; }
        if (segmentName.length > MAX_NAME) { setNameError(`Max ${MAX_NAME} characters allowed.`); return; }
        if (segmentDesc.length > MAX_DESC) { setDescError(`Max ${MAX_DESC} characters allowed.`); return; }
        if (rules.length === 0) { setRuleError('Please add at least one rule.'); return; }
        const validation = validateRules(rules, staticFields);
        if (!validation.valid) { setRuleError(validation.message); return; }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/segmentation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ name: segmentName, description: segmentDesc, segment_json: rules }),
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                toast.error(err.error || 'Error creating segment.', { autoClose: 3000 });
                return;
            }
            toast.success('Segment created successfully!', { autoClose: 2000 });
            setTimeout(() => navigate('/segmentation'), 2500);
        } catch (error) {
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                <ToastContainer />
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Create Segment</h5>
                        <p style={styles.pageSubtitle}>Define rules to segment your contacts</p>
                    </div>
                    <button style={styles.btnSecondary} onClick={() => navigate('/segmentation')}>
                        <i className="bi bi-arrow-left" style={{ marginRight: 6 }}></i>Back
                    </button>
                </div>

                <div style={styles.layout}>
                    {/* Left — Form */}
                    <div style={styles.leftCol}>
                        <div style={styles.card}>
                            {/* Segment Name */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>
                                    Segment Name
                                    <span style={styles.charCount}>{MAX_NAME - segmentName.length}/{MAX_NAME}</span>
                                </label>
                                <input type="text" value={segmentName} placeholder="Enter segment name"
                                    maxLength={MAX_NAME + 1}
                                    style={{ ...styles.input, ...(nameError ? styles.inputError : {}) }}
                                    onChange={e => { setSegmentName(e.target.value); setNameError(e.target.value.length > MAX_NAME ? `Max ${MAX_NAME} characters.` : ''); }} />
                                {nameError && <span style={styles.fieldError}>{nameError}</span>}
                            </div>

                            {/* Description */}
                            <div style={styles.fieldGroup}>
                                <label style={styles.label}>
                                    Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                                    <span style={styles.charCount}>{MAX_DESC - segmentDesc.length}/{MAX_DESC}</span>
                                </label>
                                <textarea value={segmentDesc} placeholder="Enter segment description" rows={3}
                                    maxLength={MAX_DESC + 1}
                                    style={{ ...styles.input, resize: 'vertical', ...(descError ? styles.inputError : {}) }}
                                    onChange={e => { setSegmentDesc(e.target.value); setDescError(e.target.value.length > MAX_DESC ? `Max ${MAX_DESC} characters.` : ''); }} />
                                {descError && <span style={styles.fieldError}>{descError}</span>}
                            </div>

                            {/* Rules */}
                            <div style={styles.rulesHeader}>
                                <label style={styles.label}>Rules</label>
                                <button style={styles.btnAddRule} onClick={() => setRules([...rules, createRuleNode()])}>
                                    <i className="bi bi-plus-lg" style={{ marginRight: 5 }}></i>Add Rule
                                </button>
                            </div>

                            {rules.length === 0 ? (
                                <div style={styles.emptyRules}>
                                    <i className="bi bi-funnel" style={{ fontSize: 24, color: '#d1d5db' }}></i>
                                    <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>No rules added yet. Click "Add Rule" to start.</p>
                                </div>
                            ) : (
                                rules.map((rule, idx) => (
                                    <RuleNode key={idx} node={rule} isRoot={true} staticFields={staticFields}
                                        onChange={updated => { const r = [...rules]; r[idx] = updated; setRules(r); }}
                                        onDelete={() => setRules(rules.filter((_, i) => i !== idx))} />
                                ))
                            )}

                            {ruleError && (
                                <div style={styles.errorBanner}>
                                    <i className="bi bi-exclamation-triangle" style={{ marginRight: 8 }}></i>{ruleError}
                                </div>
                            )}

                            <div style={styles.actions}>
                                <button style={styles.btnSecondary} onClick={() => navigate('/segmentation')}>Cancel</button>
                                <button style={styles.btnPrimary} onClick={handleSubmit}
                                    disabled={loading || rules.length === 0 || !!nameError || !!descError || !segmentName.trim()}>
                                    {loading ? <><span style={styles.spinner}></span> Creating…</> : 'Create Segment'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right — Preview */}
                    <div style={styles.rightCol}>
                        <div style={styles.previewCard}>
                            <h6 style={styles.previewTitle}>
                                <i className="bi bi-diagram-3" style={{ marginRight: 8, color: '#1565c0' }}></i>
                                Rule Preview
                            </h6>
                            <RuleTreePreview rules={rules} />
                        </div>
                    </div>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    layout: { display: 'flex', gap: '16px', alignItems: 'flex-start' },
    leftCol: { flex: 2 },
    rightCol: { flex: 1, position: 'sticky', top: 16 },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    previewCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    previewTitle: { margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center' },
    fieldGroup: { marginBottom: '18px', display: 'flex', flexDirection: 'column', gap: '5px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    charCount: { fontSize: '11px', color: '#9ca3af', fontWeight: 400 },
    input: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827', fontFamily: 'inherit' },
    inputError: { borderColor: '#ef4444' },
    fieldError: { fontSize: '12px', color: '#ef4444' },
    rulesHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
    btnAddRule: { display: 'inline-flex', alignItems: 'center', padding: '6px 12px', background: '#eff6ff', color: '#1565c0', border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    emptyRules: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '32px 20px', background: '#f9fafb', borderRadius: '8px', border: '2px dashed #e5e7eb', marginBottom: 16 },
    errorBanner: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', color: '#dc2626', marginBottom: '16px', display: 'flex', alignItems: 'center' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { display: 'inline-flex', alignItems: 'center', padding: '10px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    spinner: { width: '15px', height: '15px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' },
};

export default CreateSegmentComponent;
